import { convertXML } from "simple-xml-to-json";
import artworkAPIRequest from "./artworkAPIRequest";
import { sleep } from "./sleep";

export type TDelegate = {
  title: string;
  artist: string;
  artWork: string | null;
};

interface IArtworkAPIRequestDelegate {
  delegate: ({ title, artist, artWork }: TDelegate) => void;
}

interface IArtworkRequestManager extends IArtworkAPIRequestDelegate {
  delay?: number;
}

interface ScheduleRoot {
  Schedule: {
    System: string; // pl. "Jazler"
    children: EventWrapper[];
  };
}

interface EventWrapper {
  Event: {
    status: "happening" | "upcoming" | "finished"; // bővíthető
    startTime: string; // "HH:MM:SS"
    eventType: "song" | "ad" | "show"; // bővíthető
    children: (AnnouncementWrapper | SongWrapper)[];
  };
}

interface AnnouncementWrapper {
  Announcement: {
    Display: string; // pl. "Now On Air:"
  };
}

interface SongWrapper {
  Song: {
    title: string;
    children: (
      | ArtistWrapper
      | JazlerWrapper
      | PlayListerWrapper
      | MediaWrapper
      | ExpireWrapper
    )[];
  };
}

interface ArtistWrapper {
  Artist: {
    name: string;
  };
}

interface JazlerWrapper {
  Jazler: {
    ID: string;
  };
}

interface PlayListerWrapper {
  PlayLister: {
    ID: string;
  };
}

interface MediaWrapper {
  Media: {
    runTime: string; // "HH:MM:SS"
  };
}

interface ExpireWrapper {
  Expire: {
    Time: string; // "HH:MM:SS"
  };
}

interface JazlerWrapper {
  Jazler: {
    ID: string; // "HH:MM:SS"
  };
}

function toMs(time: string) {
  let [h, m, s] = time.split(":").map(Number);
  return (h * 3600 + m * 60 + s) * 1000;
}

function diffToExpire(expire: string) {
  const now = toMs(getCurrentTime());
  let target = toMs(expire);
  if (target < now) {
    target += 24 * 3600 * 1000; // ha átnyúlik másnapra
  }
  return target - now;
}

function getCurrentTime() {
  let now = new Date();
  let hours = String(now.getHours()).padStart(2, "0");
  let minutes = String(now.getMinutes()).padStart(2, "0");
  let seconds = String(now.getSeconds()).padStart(2, "0");

  return `${hours}:${minutes}:${seconds}`;
}

const artworkRequestManager = async ({
  delegate,
  delay = 0,
}: IArtworkRequestManager) => {
  const maxDelay = 1000 * 60 * 15;
  const delayCorrection = 1000 * 60 * 5;
  const minDelay = 1000 * 10;

  let firstRun = true;

  while (true) {
    if (!firstRun) {
      console.log(`sleep: ${delay}`);
      await sleep(Math.max(minDelay, Math.min(maxDelay, delay)));
    } else {
      console.log("sleep: 0 (first run)");
      firstRun = false;
    }

    try {
      const xmlText = await artworkAPIRequest({});
      if (!xmlText) {
        delay = delayCorrection;
        continue;
      }

      const json = convertXML(xmlText) as ScheduleRoot;
      if (!json?.Schedule?.children?.length) {
        throw new Error("No schedule data");
      }

      const event = json.Schedule.children[0].Event;
      let artist = "X Music";
      let title = "Érezd magad jól!";
      let artWork = null;
      let expireTime: string | null = null;

      const songChild = event?.children?.find(
        (c): c is SongWrapper => "Song" in c
      );
      if (songChild) {
        title = songChild.Song.title ?? title;
        const artistChild = songChild.Song.children.find(
          (c): c is ArtistWrapper => "Artist" in c
        );
        if (artistChild) {
          artist = artistChild.Artist.name ?? artist;
        }
        const expireChild = songChild.Song.children.find(
          (c): c is ExpireWrapper => "Expire" in c
        );
        if (expireChild) {
          expireTime = expireChild.Expire.Time;
        }
        const jazlerChild = songChild.Song.children.find(
          (c): c is JazlerWrapper => "Jazler" in c
        );
        if (jazlerChild) {
          artWork = jazlerChild.Jazler.ID;
        }
      }

      if (expireTime) {
        let localExpireTime = diffToExpire(expireTime);
        if (localExpireTime <= 0) {
          delay = delayCorrection;
        } else {
          delay = Math.min(localExpireTime, maxDelay);
        }
      } else {
        delay = delayCorrection;
      }

      console.log(`feltöltötte: ${artist} - ${title}`);
      delegate({ artist, title, artWork });
    } catch (e) {
      console.error("Artwork manager error:", e);
      delay = delayCorrection;
      delegate({ artist: "X Music", title: "Érezd magad jól!", artWork: null });
    }
  }
};

export default artworkRequestManager;
